import { useState, useEffect } from "react";
import { useAuth, downloadAuthFile } from "@/contexts/AuthContext";
import { usePagination, PaginationBar } from "@/components/PaginationBar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users, FileText, Briefcase, Newspaper, Bell, BarChart3, LogOut, User, Menu, X, ClipboardList, MessageSquare, Search, CheckCircle, XCircle, Send, Loader2, Download, ArrowLeft, Reply, FolderDown, Upload, CalendarDays, Plus, Trash2, Clock, Scale, AlertTriangle, Eye, Bot, ChevronRight, ScrollText, MapPin, Lock, Mail } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import UserManagement from "@/components/admin/UserManagement";
import BandiManagement from "@/components/admin/BandiManagement";
import ProjectManagement from "@/components/admin/ProjectManagement";
import NewsManagement from "@/components/admin/NewsManagement";
import MemberProfile from "@/components/member/MemberProfile";
import MemberBandi from "@/components/member/MemberBandi";
import MemberProjects from "@/components/member/MemberProjects";
import MemberNews from "@/components/member/MemberNews";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const UGL_LOGO = "/ugl-logo.png";

export default function Dashboard() {
  const { user, logout } = useAuth();
  const isAdmin = user?.ruolo === "admin" || user?.ruolo === "helper";
  const [activeTab, setActiveTab] = useState(isAdmin ? "stats" : "profilo");
  const [stats, setStats] = useState(null);
  const [notifCount, setNotifCount] = useState(0);
  const [msgCount, setMsgCount] = useState(0);
  const [notifiche, setNotifiche] = useState([]);
  const [tesseramenti, setTesseramenti] = useState([]);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (isAdmin) {
      axios.get(`${API}/stats`).then(r => setStats(r.data)).catch(() => {});
      axios.get(`${API}/notifiche/count`).then(r => setNotifCount(r.data.count)).catch(() => {});
    }
    axios.get(`${API}/messaggi/non-letti`).then(r => setMsgCount(r.data.count)).catch(() => {});
  }, [isAdmin]);

  useEffect(() => {
    if (activeTab === "notifiche" && isAdmin) axios.get(`${API}/notifiche`).then(r => setNotifiche(r.data)).catch(() => {});
    if (activeTab === "tesseramenti" && isAdmin) axios.get(`${API}/tesseramenti`).then(r => setTesseramenti(r.data)).catch(() => {});
  }, [activeTab, isAdmin]);

  const markRead = async (id) => {
    await axios.put(`${API}/notifiche/${id}/letto`, {});
    setNotifiche(prev => prev.map(n => n.id === id ? { ...n, letto: true } : n));
    setNotifCount(prev => Math.max(0, prev - 1));
  };

  const updateTessStato = async (id, stato) => {
    try {
      await axios.put(`${API}/tesseramenti/${id}/stato`, { stato });
      setTesseramenti(prev => prev.map(t => t.id === id ? { ...t, stato } : t));
      toast.success(`Tesseramento ${stato}`);
    } catch { toast.error("Errore aggiornamento"); }
  };

  const adminTabs = [
    { id: "stats", label: "Dashboard", icon: BarChart3 },
    { id: "utenti", label: "Utenti", icon: Users },
    { id: "bandi", label: "Bandi", icon: FileText },
    { id: "progetti", label: "Progetti", icon: Briefcase },
    { id: "notizie", label: "Notizie", icon: Newspaper },
    { id: "calendario", label: "Calendario", icon: CalendarDays },
    { id: "tesseramenti", label: "Tesseramenti", icon: ClipboardList },
    { id: "segnalazioni", label: "Segnalazioni", icon: AlertTriangle },
    { id: "messaggi", label: "Messaggi", icon: MessageSquare, badge: msgCount },
    { id: "notifiche", label: "Notifiche", icon: Bell, badge: notifCount },
    { id: "audit", label: "Audit Log", icon: ScrollText },
    { id: "email", label: "Email", icon: Mail },
  ];

  const memberTabsUGL = [
    { id: "profilo", label: "Profilo & CV", icon: User },
    { id: "bandi", label: "Bandi", icon: FileText },
    { id: "progetti", label: "Progetti", icon: Briefcase },
    { id: "notizie", label: "Notizie", icon: Newspaper },
    { id: "calendario", label: "Calendario", icon: CalendarDays },
    { id: "assistenza", label: "Assistenza", icon: Scale },
    { id: "download", label: "Documenti", icon: FolderDown },
    { id: "messaggi", label: "Messaggi", icon: MessageSquare, badge: msgCount },
  ];

  const memberTabsBase = [
    { id: "profilo", label: "Profilo & CV", icon: User },
    { id: "calendario", label: "Calendario", icon: CalendarDays },
    { id: "assistenza", label: "Assistenza", icon: Scale },
    { id: "download", label: "Documenti", icon: FolderDown },
  ];

  const isBase = user?.ruolo === "iscritto_base";
  const tabs = isAdmin ? adminTabs : isBase ? memberTabsBase : memberTabsUGL;

  const renderContent = () => {
    if (activeTab === "messaggi") return <MessaggiView userId={user?.id} />;
    if (activeTab === "calendario") return <CalendarioView isAdmin={isAdmin} />;
    if (isAdmin) {
      switch (activeTab) {
        case "stats": return <StatsView stats={stats} />;
        case "utenti": return <UserManagement userRole={user?.ruolo} />;
        case "bandi": return <BandiManagement userRole={user?.ruolo} />;
        case "progetti": return <ProjectManagement userRole={user?.ruolo} />;
        case "notizie": return <NewsManagement userRole={user?.ruolo} />;
        case "tesseramenti": return <TesseramentiView data={tesseramenti} onUpdate={updateTessStato} />;
        case "segnalazioni": return <SegnalazioniAdminView />;
        case "notifiche": return <NotificheView notifiche={notifiche} markRead={markRead} />;
        case "audit": return <AuditLogView />;
        case "email": return (
          <div className="flex items-center justify-center h-[60vh]">
            <div className="text-center max-w-sm">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#1a3050] to-[#1e3a5c] border border-[#2a5080] flex items-center justify-center mx-auto mb-5">
                <Mail className="w-8 h-8 text-[#5ba8d9]" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2" style={{ fontFamily: 'Outfit' }}>Webmail UGL Lariano</h3>
              <p className="text-sm text-white/40 mb-6">Accedi alla posta elettronica del sindacato</p>
              <a href="https://mail.ugllariano.it/" target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#C9A84C] text-[#001830] hover:bg-[#C9A84C]/90 font-semibold px-8 h-11">
                  <Mail className="w-4 h-4 mr-2" /> Apri Webmail
                </Button>
              </a>
              <p className="text-[10px] text-white/20 mt-4">Si apre in una nuova finestra</p>
            </div>
          </div>
        );
        default: return <StatsView stats={stats} />;
      }
    }
    switch (activeTab) {
      case "profilo": return <MemberProfile />;
      case "bandi": return <MemberBandi />;
      case "progetti": return <MemberProjects />;
      case "notizie": return <MemberNews />;
      case "assistenza": return <AssistenzaView />;
      case "download": return <PinGate userId={user?.id}><DocumentiView userId={user?.id} userRole={user?.ruolo} /></PinGate>;
      default: return <MemberProfile />;
    }
  };

  return (
    <div className="min-h-screen bg-[#0f1d32] flex" data-testid="dashboard-container">
      {mobileOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setMobileOpen(false)} />}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-[#13243d] border-r border-[#1e3456] flex flex-col transition-transform lg:translate-x-0 ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`} data-testid="dashboard-sidebar">
        <div className="p-5 border-b border-[#1e3456]">
          <div className="flex items-center gap-2.5">
            <img src={UGL_LOGO} alt="UGL" className="w-9 h-9 object-contain" />
            <div>
              <p className="font-semibold text-sm text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>UGL Lariano</p>
              <p className="text-[10px] text-[#C9A84C]/60 uppercase tracking-wider">Portale Sindacale</p>
            </div>
          </div>
        </div>
        <ScrollArea className="flex-1 py-3">
          <nav className="px-3 space-y-1">
            {tabs.map(tab => {
              const iconColors = { stats: "text-[#5ba8d9]", utenti: "text-[#C9A84C]", bandi: "text-emerald-400", progetti: "text-purple-400", notizie: "text-pink-400", calendario: "text-amber-400", tesseramenti: "text-teal-400", segnalazioni: "text-red-400", messaggi: "text-[#5ba8d9]", notifiche: "text-[#C9A84C]", profilo: "text-[#C9A84C]", assistenza: "text-red-400", download: "text-emerald-400" };
              return (
              <button key={tab.id} data-testid={`nav-${tab.id}-link`}
                onClick={() => { setActiveTab(tab.id); setMobileOpen(false); }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${activeTab === tab.id ? 'bg-gradient-to-r from-[#1a3050] to-[#1e3a5c] text-white border border-[#2a5080] shadow-md shadow-black/20' : 'text-white/50 hover:text-white hover:bg-[#1a2d48] border border-transparent'}`}>
                <tab.icon className={`w-4 h-4 flex-shrink-0 ${activeTab === tab.id ? 'text-white' : iconColors[tab.id] || 'text-white/40'}`} />
                {tab.label}
                {tab.badge > 0 && <Badge className="ml-auto bg-red-500 text-white text-[10px] px-1.5 py-0 border-0 shadow-sm shadow-red-500/30">{tab.badge}</Badge>}
              </button>
            )})}
          </nav>
        </ScrollArea>
        <div className="p-4 border-t border-[#1e3456]">
          <div className="flex items-center gap-2.5 mb-3">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#C9A84C]/20 to-[#C9A84C]/5 border border-[#C9A84C]/25 flex items-center justify-center">
              <User className="w-4 h-4 text-[#C9A84C]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-white truncate">{user?.nome} {user?.cognome}</p>
              <p className="text-[10px] text-[#C9A84C]/50 uppercase">{user?.ruolo}</p>
            </div>
          </div>
          <Button data-testid="logout-btn" onClick={() => logout()} variant="outline" size="sm"
            className="w-full text-white/40 border-white/10 hover:bg-red-500/10 hover:text-red-400 hover:border-red-500/20 bg-transparent">
            <LogOut className="w-3.5 h-3.5 mr-2" /> Esci
          </Button>
        </div>
      </aside>
      <main className="flex-1 min-w-0">
        <header className="bg-[#13243d] border-b border-[#1e3456] sticky top-0 z-30 h-14 flex items-center px-4 lg:px-8 gap-4">
          <button data-testid="mobile-menu-btn" className="lg:hidden p-1.5 rounded-md hover:bg-[#1e3456] text-white/50" onClick={() => setMobileOpen(!mobileOpen)}>
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          <div className="w-1 h-5 rounded-full bg-[#C9A84C]" />
          <h2 className="text-base font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {tabs.find(t => t.id === activeTab)?.label || "Dashboard"}
          </h2>
        </header>
        <div className="p-4 lg:p-8">{renderContent()}</div>
      </main>
    </div>
  );
}

/* ── Stats with Charts ── */
function StatsView({ stats }) {
  if (!stats) return <div className="text-center py-12 text-white/30">Caricamento...</div>;
  const cards = [
    { label: "Utenti", value: stats.utenti, icon: Users, color: "bg-gradient-to-br from-[#1a3a5c] to-[#0f2a45] border-[#2a6090]", ic: "text-[#5ba8d9]" },
    { label: "Bandi", value: stats.bandi, icon: FileText, color: "bg-gradient-to-br from-[#3a2d1a] to-[#2a2010] border-[#6b5430]", ic: "text-[#C9A84C]" },
    { label: "Progetti", value: stats.progetti, icon: Briefcase, color: "bg-gradient-to-br from-[#1a3a2a] to-[#0f2a1a] border-[#2a6040]", ic: "text-emerald-400" },
    { label: "Notizie", value: stats.notizie, icon: Newspaper, color: "bg-gradient-to-br from-[#2a1a3a] to-[#1a1028] border-[#4a3060]", ic: "text-purple-400" },
  ];

  // Build chart data from monthly registrations
  const monthlyData = Object.entries(stats.registrazioni_mensili || {}).sort().slice(-6).map(([k, v]) => {
    const [y, m] = k.split('-');
    const label = new Date(parseInt(y), parseInt(m) - 1).toLocaleDateString('it-IT', { month: 'short' });
    return { mese: label, utenti: v };
  });

  return (
    <div data-testid="admin-stats-view" className="space-y-6">
      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c, i) => (
          <div key={i} className={`rounded-xl border p-5 ${c.color} transition-all hover:-translate-y-0.5`} data-testid={`stat-card-${i}`}>
            <div className="flex items-center gap-3 mb-3"><c.icon className={`w-5 h-5 ${c.ic}`} /><p className="text-[10px] font-semibold tracking-wider uppercase text-white/40">{c.label}</p></div>
            <p className="text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>{c.value}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Chart - Registrazioni mensili */}
        <div className="bg-[#13243d] border border-[#1e3456] rounded-xl p-5">
          <h3 className="text-sm font-semibold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Registrazioni Mensili</h3>
          {monthlyData.length > 0 ? (
            <div className="flex items-end gap-3 h-32">
              {monthlyData.map((d, i) => {
                const max = Math.max(...monthlyData.map(x => x.utenti), 1);
                const h = (d.utenti / max) * 100;
                return (
                  <div key={i} className="flex-1 flex flex-col items-center gap-1">
                    <span className="text-xs text-[#5ba8d9] font-bold">{d.utenti}</span>
                    <div className="w-full rounded-t-md bg-[#1a3a5c] relative overflow-hidden" style={{ height: `${Math.max(h, 8)}%` }}>
                      <div className="absolute inset-0 bg-[#2a6090] rounded-t-md" />
                    </div>
                    <span className="text-[9px] text-white/30 uppercase">{d.mese}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-white/25 text-center py-8">Dati non disponibili</p>
          )}
        </div>

        {/* Prossimi eventi */}
        <div className="bg-[#13243d] border border-[#1e3456] rounded-xl p-5">
          <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <CalendarDays className="w-4 h-4 text-[#C9A84C]" /> Prossimi Eventi
          </h3>
          {(stats.eventi_prossimi || []).length > 0 ? (
            <div className="space-y-3">
              {stats.eventi_prossimi.map((e, i) => (
                <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg bg-[#0f1d32] border border-[#1e3456]">
                  <div className="w-10 h-10 rounded-lg bg-[#1a3a5c] flex flex-col items-center justify-center flex-shrink-0">
                    <p className="text-sm font-bold text-[#5ba8d9] leading-none">{new Date(e.data + 'T00:00').getDate()}</p>
                    <p className="text-[7px] uppercase text-[#5ba8d9]/60">{new Date(e.data + 'T00:00').toLocaleDateString('it-IT', { month: 'short' })}</p>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-white truncate">{e.titolo}</p>
                    <p className="text-[10px] text-white/30">{e.ora && `${e.ora} - `}{e.luogo || ''}</p>
                  </div>
                  <Badge className="bg-[#1a3a5c] text-[#5ba8d9] border-[#2a5080] text-[9px] border">{e.tipo}</Badge>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-white/25 text-center py-8">Nessun evento in programma</p>
          )}
        </div>
      </div>

      {/* Alert cards */}
      <div className="flex flex-wrap gap-3">
        {stats.notifiche_non_lette > 0 && (
          <div className="bg-[#13243d] border border-[#C9A84C]/20 rounded-xl p-4 flex items-center gap-3 flex-1 min-w-[200px]">
            <Bell className="w-4 h-4 text-[#C9A84C]" /><p className="text-sm text-white/70"><strong className="text-[#C9A84C]">{stats.notifiche_non_lette}</strong> notifiche</p>
          </div>
        )}
        {stats.tesseramenti_attesa > 0 && (
          <div className="bg-[#13243d] border border-amber-500/20 rounded-xl p-4 flex items-center gap-3 flex-1 min-w-[200px]">
            <ClipboardList className="w-4 h-4 text-amber-400" /><p className="text-sm text-white/70"><strong className="text-amber-400">{stats.tesseramenti_attesa}</strong> tesseramenti</p>
          </div>
        )}
        {stats.segnalazioni_nuove > 0 && (
          <div className="bg-[#13243d] border border-red-500/20 rounded-xl p-4 flex items-center gap-3 flex-1 min-w-[200px]">
            <AlertTriangle className="w-4 h-4 text-red-400" /><p className="text-sm text-white/70"><strong className="text-red-400">{stats.segnalazioni_nuove}</strong> segnalazioni</p>
          </div>
        )}
      </div>

      {/* ═══ ANALYTICS PREMIUM ═══ */}
      <AnalyticsDashboard />

      {/* Export */}
      <div className="bg-[#13243d] border border-[#1e3456] rounded-xl p-5">
        <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          <Download className="w-4 h-4 text-[#5ba8d9]" /> Esporta Dati (CSV)
        </h3>
        <div className="flex flex-wrap gap-2">
          {[{l:"Utenti",k:"utenti"},{l:"Bandi",k:"bandi"},{l:"Progetti",k:"progetti"},{l:"Tesseramenti",k:"tesseramenti"}].map(e => (
            <Button key={e.k} size="sm" variant="outline" onClick={() => downloadAuthFile(`${API}/export/${e.k}`, `${e.k}_ugl.xlsx`)} data-testid={`export-${e.k}`}
              className="border-[#1e3456] text-white/50 bg-transparent hover:bg-[#1a3a5c] hover:text-white text-xs">
              <Download className="w-3 h-3 mr-1.5" /> {e.l}
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── Notifiche ── */
function NotificheView({ notifiche, markRead }) {
  const [search, setSearch] = useState("");
  const searched = notifiche.filter(n => n.messaggio.toLowerCase().includes(search.toLowerCase()));
  const pg = usePagination(searched);
  return (
    <div data-testid="notifiche-list">
      <div className="mb-4 relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/20" /><Input placeholder="Cerca notifiche..." value={search} onChange={e => { setSearch(e.target.value); pg.resetPage(); }} className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/20" data-testid="search-notifiche" /></div>
      {pg.paged.length === 0 && <p className="text-center py-12 text-white/30">{search ? "Nessun risultato" : "Nessuna notifica"}</p>}
      <div className="space-y-3">
        {pg.paged.map(n => (
          <div key={n.id} className={`glass-card rounded-lg border p-4 flex items-start gap-4 ${n.letto ? 'border-white/5 opacity-50' : 'border-[#C9A84C]/20'}`} data-testid={`notifica-${n.id}`}>
            <Bell className={`w-5 h-5 mt-0.5 flex-shrink-0 ${n.letto ? 'text-white/20' : 'text-[#C9A84C]'}`} />
            <div className="flex-1"><p className="text-sm font-medium text-white/80">{n.messaggio}</p><p className="text-xs text-white/30 mt-1">{new Date(n.created_at).toLocaleString('it-IT')}</p></div>
            {!n.letto && <Button size="sm" variant="outline" onClick={() => markRead(n.id)} className="text-xs border-white/10 text-white/50 hover:bg-white/5 bg-transparent" data-testid={`mark-read-${n.id}`}>Letto</Button>}
          </div>
        ))}
      </div>
      <PaginationBar {...pg} />
    </div>
  );
}

/* ── Tesseramenti con Approva/Rifiuta ── */
function TesseramentiView({ data, onUpdate }) {
  const [search, setSearch] = useState("");
  const searched = data.filter(t => `${t.nome} ${t.cognome} ${t.codice_fiscale}`.toLowerCase().includes(search.toLowerCase()));
  const pg = usePagination(searched);
  const sc = { in_attesa: "bg-amber-500/10 text-amber-400 border-amber-500/20", approvato: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20", rifiutato: "bg-red-500/10 text-red-400 border-red-500/20" };
  return (
    <div data-testid="tesseramenti-list">
      <div className="mb-4 relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/20" /><Input placeholder="Cerca per nome o CF..." value={search} onChange={e => { setSearch(e.target.value); pg.resetPage(); }} className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/20" data-testid="search-tesseramenti" /></div>
      {pg.paged.length === 0 && <p className="text-center py-12 text-white/30">{search ? "Nessun risultato" : "Nessuna richiesta"}</p>}
      <div className="space-y-3">
        {pg.paged.map(t => (
          <div key={t.id} className="glass-card rounded-lg border border-white/10 p-5" data-testid={`tess-${t.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <p className="text-base font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>{t.nome} {t.cognome}</p>
                <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-white/40">
                  <span className="font-mono">{t.codice_fiscale}</span><span>{t.telefono}</span>{t.email && <span>{t.email}</span>}
                </div>
                {t.messaggio && <p className="text-sm text-white/50 mt-2">{t.messaggio}</p>}
                <p className="text-[10px] text-white/20 mt-2">{new Date(t.created_at).toLocaleString('it-IT')}</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {t.stato === "in_attesa" ? (
                  <>
                    <Button size="sm" onClick={() => onUpdate(t.id, "approvato")} className="bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25 border border-emerald-500/20" data-testid={`approve-tess-${t.id}`}>
                      <CheckCircle className="w-3.5 h-3.5 mr-1.5" /> Approva
                    </Button>
                    <Button size="sm" onClick={() => onUpdate(t.id, "rifiutato")} className="bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/20" data-testid={`reject-tess-${t.id}`}>
                      <XCircle className="w-3.5 h-3.5 mr-1.5" /> Rifiuta
                    </Button>
                  </>
                ) : (
                  <Badge className={`${sc[t.stato] || sc.in_attesa} text-xs border`}>{t.stato}</Badge>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      <PaginationBar {...pg} />
    </div>
  );
}

/* ── Messaggistica con Thread ── */
function MessaggiView({ userId }) {
  const [messaggi, setMessaggi] = useState([]);
  const [utenti, setUtenti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [newMsg, setNewMsg] = useState({ destinatario_id: "", oggetto: "", testo: "", thread_id: null });
  const [sending, setSending] = useState(false);
  const [search, setSearch] = useState("");
  const [openThread, setOpenThread] = useState(null);
  const [threadMsgs, setThreadMsgs] = useState([]);
  const [replyText, setReplyText] = useState("");

  const fetchAll = async () => {
    try {
      const [m, u] = await Promise.all([
        axios.get(`${API}/messaggi`),
        axios.get(`${API}/messaggi/utenti`),
      ]);
      setMessaggi(m.data); setUtenti(u.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, []);

  const sendMsg = async () => {
    if (!newMsg.destinatario_id || !newMsg.oggetto || !newMsg.testo) { toast.error("Compila tutti i campi"); return; }
    setSending(true);
    try {
      await axios.post(`${API}/messaggi`, newMsg);
      toast.success("Messaggio inviato!"); setShowNew(false); setNewMsg({ destinatario_id: "", oggetto: "", testo: "", thread_id: null }); fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore invio"); }
    setSending(false);
  };

  const openThreadView = async (threadId) => {
    setOpenThread(threadId);
    try { const { data } = await axios.get(`${API}/messaggi/thread/${threadId}`); setThreadMsgs(data); } catch (e) { console.error(e); }
  };

  const sendReply = async () => {
    if (!replyText.trim() || !threadMsgs.length) return;
    const orig = threadMsgs[0];
    const destId = orig.mittente_id === userId ? orig.destinatario_id : orig.mittente_id;
    setSending(true);
    try {
      await axios.post(`${API}/messaggi`, { destinatario_id: destId, oggetto: `Re: ${orig.oggetto}`, testo: replyText, thread_id: openThread });
      setReplyText(""); openThreadView(openThread); fetchAll(); toast.success("Risposta inviata!");
    } catch { toast.error("Errore"); }
    setSending(false);
  };

  // Group by thread
  const threads = {};
  messaggi.forEach(m => {
    const tid = m.thread_id || m.id;
    if (!threads[tid]) threads[tid] = { id: tid, latest: m, count: 1, unread: (!m.letto && m.destinatario_id === userId) ? 1 : 0 };
    else { threads[tid].count++; if (!m.letto && m.destinatario_id === userId) threads[tid].unread++; if (m.created_at > threads[tid].latest.created_at) threads[tid].latest = m; }
  });
  const threadList = Object.values(threads).sort((a, b) => b.latest.created_at.localeCompare(a.latest.created_at));
  const searched = threadList.filter(t => `${t.latest.oggetto} ${t.latest.mittente_nome} ${t.latest.destinatario_nome}`.toLowerCase().includes(search.toLowerCase()));
  // Add created_at to threads for date filter
  const searchedWithDate = searched.map(t => ({ ...t, created_at: t.latest.created_at }));
  const pg = usePagination(searchedWithDate);

  if (loading) return <div className="text-center py-12 text-white/30">Caricamento...</div>;

  // Thread detail view
  if (openThread) {
    const other = threadMsgs[0] ? (threadMsgs[0].mittente_id === userId ? threadMsgs[0].destinatario_nome : threadMsgs[0].mittente_nome) : "";
    return (
      <div data-testid="thread-view">
        <button onClick={() => { setOpenThread(null); setThreadMsgs([]); }} className="flex items-center gap-1.5 text-sm text-white/40 hover:text-white mb-4" data-testid="back-to-messages">
          <ArrowLeft className="w-4 h-4" /> Torna ai messaggi
        </button>
        <div className="glass-card rounded-lg border border-[#C9A84C]/15 p-4 mb-4">
          <p className="text-base font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>{threadMsgs[0]?.oggetto}</p>
          <p className="text-xs text-white/30 mt-1">Conversazione con {other}</p>
        </div>
        <div className="space-y-3 mb-4 max-h-[400px] overflow-y-auto pr-2">
          {threadMsgs.map(m => {
            const isMine = m.mittente_id === userId;
            return (
              <div key={m.id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[70%] rounded-lg p-3 ${isMine ? 'bg-[#00A0D1]/10 border border-[#00A0D1]/20' : 'bg-white/5 border border-white/10'}`}>
                  <p className="text-xs text-white/30 mb-1">{m.mittente_nome} &middot; {new Date(m.created_at).toLocaleString('it-IT', { day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit'})}</p>
                  <p className="text-sm text-white/80">{m.testo}</p>
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex gap-2">
          <Input value={replyText} onChange={e => setReplyText(e.target.value)} placeholder="Scrivi una risposta..."
            className="bg-white/5 border-white/10 text-white placeholder:text-white/20" data-testid="reply-input"
            onKeyDown={e => e.key === 'Enter' && sendReply()} />
          <Button onClick={sendReply} disabled={sending} className="bg-[#C9A84C] text-[#001830] hover:bg-[#C9A84C]/90" data-testid="send-reply-btn">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="messaggi-view">
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/20" /><Input placeholder="Cerca messaggi..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/20" data-testid="search-messaggi" /></div>
        <Button onClick={() => setShowNew(true)} className="bg-[#C9A84C] text-[#001830] hover:bg-[#C9A84C]/90 font-medium" data-testid="new-message-btn">
          <Send className="w-4 h-4 mr-2" /> Nuovo
        </Button>
      </div>
      {pg.paged.length === 0 && <p className="text-center py-12 text-white/30">{search ? "Nessun risultato" : "Nessun messaggio"}</p>}
      <div className="space-y-2">
        {pg.paged.map(t => {
          const m = t.latest;
          const isMine = m.mittente_id === userId;
          return (
            <div key={t.id} onClick={() => openThreadView(t.id)}
              className={`glass-card rounded-lg border p-4 cursor-pointer transition-all hover:border-[#00A0D1]/20 ${t.unread ? 'border-[#C9A84C]/20 bg-[#C9A84C]/[0.03]' : 'border-white/8'}`} data-testid={`thread-${t.id}`}>
              <div className="flex items-start gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold ${isMine ? 'bg-[#00A0D1]/10 text-[#00A0D1] border border-[#00A0D1]/20' : 'bg-[#C9A84C]/10 text-[#C9A84C] border border-[#C9A84C]/20'}`}>
                  {isMine ? "TU" : m.mittente_nome?.split(' ').map(w => w[0]).join('').slice(0,2)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-white truncate">{m.oggetto}</p>
                    {t.unread > 0 && <Badge className="bg-[#C9A84C] text-[#001830] text-[10px] px-1.5 py-0 border-0">{t.unread}</Badge>}
                    {t.count > 1 && <span className="text-[10px] text-white/20">{t.count} msg</span>}
                  </div>
                  <p className="text-xs text-white/30 mt-0.5">
                    {isMine ? `A: ${m.destinatario_nome}` : `Da: ${m.mittente_nome}`}
                    <span className="mx-2 text-white/10">|</span>
                    {new Date(m.created_at).toLocaleString('it-IT', { day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit'})}
                  </p>
                  <p className="text-sm text-white/40 mt-1 line-clamp-1">{m.testo}</p>
                </div>
                <Reply className="w-4 h-4 text-white/15 flex-shrink-0" />
              </div>
            </div>
          );
        })}
      </div>
      <PaginationBar {...pg} />

      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="sm:max-w-lg bg-[#0d1b2e] border-white/10 text-white" data-testid="new-message-dialog">
          <DialogHeader>
            <DialogTitle className="text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>Nuovo Messaggio</DialogTitle>
            <DialogDescription className="text-white/40">Invia un messaggio</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs text-white/60">Destinatario *</Label>
              <Select value={newMsg.destinatario_id} onValueChange={v => setNewMsg(p => ({ ...p, destinatario_id: v }))}>
                <SelectTrigger className="mt-1 bg-white/5 border-white/10 text-white" data-testid="msg-destinatario"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                <SelectContent className="bg-[#0d1b2e] border-white/10 text-white">
                  {utenti.map(u => <SelectItem key={u.id} value={u.id}>{u.nome} {u.cognome} ({u.ruolo})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-white/60">Oggetto *</Label>
              <Input data-testid="msg-oggetto" value={newMsg.oggetto} onChange={e => setNewMsg(p => ({ ...p, oggetto: e.target.value }))} className="mt-1 bg-white/5 border-white/10 text-white" />
            </div>
            <div>
              <Label className="text-xs text-white/60">Messaggio *</Label>
              <Textarea data-testid="msg-testo" value={newMsg.testo} onChange={e => setNewMsg(p => ({ ...p, testo: e.target.value }))} className="mt-1 bg-white/5 border-white/10 text-white" rows={4} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNew(false)} className="border-white/10 text-white/50 bg-transparent hover:bg-white/5">Annulla</Button>
            <Button data-testid="send-message-btn" onClick={sendMsg} disabled={sending} className="bg-[#C9A84C] text-[#001830] hover:bg-[#C9A84C]/90">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-2" /> Invia</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


/* ── Documenti/Download ── */
function DocumentiView({ userId, userRole }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [users, setUsers] = useState([]);
  const [uploadTarget, setUploadTarget] = useState("");
  const isAdmin = userRole === "admin" || userRole === "helper";

  useEffect(() => {
    axios.get(`${API}/documenti`).then(r => setDocs(r.data)).catch(() => {}).finally(() => setLoading(false));
    if (isAdmin) axios.get(`${API}/admin/users`).then(r => setUsers(r.data)).catch(() => {});
  }, [isAdmin]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const params = new URLSearchParams({ tipo: uploadTarget ? "personale" : "generale" });
      if (uploadTarget) params.set("target_user_id", uploadTarget);
      await axios.post(`${API}/upload/documento?${params}`, fd);
      toast.success("Documento caricato!");
      const { data } = await axios.get(`${API}/documenti`);
      setDocs(data);
    } catch { toast.error("Errore upload"); }
    setUploading(false);
    e.target.value = '';
  };

  const downloadDoc = async (doc) => {
    try {
      const resp = await axios.get(`${API}/files/${doc.storage_path}`, { responseType: 'blob' });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a'); a.href = url; a.download = doc.original_filename; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Errore download"); }
  };

  const deleteDoc = async (id) => {
    if (!window.confirm("Eliminare?")) return;
    try { await axios.delete(`${API}/documenti/${id}`); setDocs(prev => prev.filter(d => d.id !== id)); toast.success("Eliminato"); } catch { toast.error("Errore"); }
  };

  const generali = docs.filter(d => d.tipo === "generale");
  const personali = docs.filter(d => d.target_user_id === userId);
  if (loading) return <div className="text-center py-12 text-white/30">Caricamento...</div>;

  return (
    <div data-testid="documenti-view">
      {isAdmin && (
        <div className="glass-card rounded-lg border border-[#C9A84C]/20 p-5 mb-6">
          <h3 className="text-sm font-semibold text-white mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Upload className="w-4 h-4 inline mr-2 text-[#C9A84C]" />Carica Documento
          </h3>
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-xs text-white/60">Per utente (vuoto = generale)</Label>
              <Select value={uploadTarget} onValueChange={setUploadTarget}>
                <SelectTrigger className="mt-1 bg-white/5 border-white/10 text-white text-sm"><SelectValue placeholder="Tutti (generale)" /></SelectTrigger>
                <SelectContent className="bg-[#0d1b2e] border-white/10 text-white">
                  <SelectItem value=" ">Tutti (generale)</SelectItem>
                  {users.filter(u => u.ruolo === "iscritto").map(u => <SelectItem key={u.id} value={u.id}>{u.nome} {u.cognome}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <label className="cursor-pointer">
              <Button as="span" className="bg-[#C9A84C] text-[#001830] hover:bg-[#C9A84C]/90" disabled={uploading} data-testid="upload-doc-btn">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />} Carica File
              </Button>
              <input type="file" onChange={handleUpload} className="hidden" />
            </label>
          </div>
        </div>
      )}

      {generali.length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-white mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Documenti Generali</h3>
          <div className="space-y-2">
            {generali.map(d => (
              <div key={d.id} className="glass-card rounded-lg border border-white/8 p-4 flex items-center gap-3">
                <FileText className="w-5 h-5 text-[#00A0D1] flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">{d.original_filename}</p>
                  <p className="text-[10px] text-white/25">di {d.uploaded_by_name} | {new Date(d.created_at).toLocaleDateString('it-IT')}</p>
                </div>
                <Button size="sm" variant="outline" onClick={() => downloadDoc(d)} className="border-white/10 text-white/50 bg-transparent hover:bg-white/5 text-xs" data-testid={`download-doc-${d.id}`}>
                  <Download className="w-3 h-3 mr-1" /> Scarica
                </Button>
                {userRole === "admin" && <Button size="sm" variant="ghost" onClick={() => deleteDoc(d.id)} className="text-white/20 hover:text-red-400"><Trash2 className="w-3 h-3" /></Button>}
              </div>
            ))}
          </div>
        </div>
      )}

      {personali.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-white mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>I Tuoi Documenti</h3>
          <div className="space-y-2">
            {personali.map(d => (
              <div key={d.id} className="glass-card rounded-lg border border-[#C9A84C]/15 p-4 flex items-center gap-3">
                <FileText className="w-5 h-5 text-[#C9A84C] flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">{d.original_filename}</p>
                  <p className="text-[10px] text-white/25">{new Date(d.created_at).toLocaleDateString('it-IT')}</p>
                </div>
                <Button size="sm" variant="outline" onClick={() => downloadDoc(d)} className="border-white/10 text-white/50 bg-transparent hover:bg-white/5 text-xs">
                  <Download className="w-3 h-3 mr-1" /> Scarica
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {!generali.length && !personali.length && <p className="text-center py-12 text-white/30">Nessun documento disponibile</p>}
    </div>
  );
}


/* ── Calendario Eventi ── */
function CalendarioView({ isAdmin }) {
  const [eventi, setEventi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ titolo: "", descrizione: "", data: "", ora: "", luogo: "", tipo: "evento" });
  const [saving, setSaving] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`; });

  useEffect(() => {
    axios.get(`${API}/eventi`).then(r => setEventi(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleCreate = async () => {
    if (!form.titolo || !form.data) { toast.error("Titolo e data sono obbligatori"); return; }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/eventi`, form);
      setEventi(prev => [...prev, data].sort((a,b) => a.data.localeCompare(b.data)));
      setShowNew(false); setForm({ titolo: "", descrizione: "", data: "", ora: "", luogo: "", tipo: "evento" });
      toast.success("Evento creato!");
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminare evento?")) return;
    try { await axios.delete(`${API}/eventi/${id}`); setEventi(prev => prev.filter(e => e.id !== id)); toast.success("Eliminato"); } catch { toast.error("Errore"); }
  };

  // Build calendar grid
  const [year, month] = selectedMonth.split('-').map(Number);
  const firstDay = new Date(year, month - 1, 1).getDay();
  const daysInMonth = new Date(year, month, 0).getDate();
  const monthName = new Date(year, month - 1).toLocaleDateString('it-IT', { month: 'long', year: 'numeric' });
  const today = new Date().toISOString().slice(0, 10);

  const eventiByDate = {};
  eventi.forEach(e => { const d = e.data?.slice(0,10); if (d) { if (!eventiByDate[d]) eventiByDate[d] = []; eventiByDate[d].push(e); }});

  const prevMonth = () => { const d = new Date(year, month - 2); setSelectedMonth(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`); };
  const nextMonth = () => { const d = new Date(year, month); setSelectedMonth(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`); };

  if (loading) return <div className="text-center py-12 text-white/30">Caricamento...</div>;

  return (
    <div data-testid="calendario-view">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button size="sm" variant="ghost" onClick={prevMonth} className="text-white/40 hover:text-white hover:bg-white/5" data-testid="cal-prev">&larr;</Button>
          <h3 className="text-base font-semibold text-white capitalize min-w-[180px] text-center" style={{ fontFamily: 'Outfit, sans-serif' }}>{monthName}</h3>
          <Button size="sm" variant="ghost" onClick={nextMonth} className="text-white/40 hover:text-white hover:bg-white/5" data-testid="cal-next">&rarr;</Button>
        </div>
        {isAdmin && (
          <Button onClick={() => setShowNew(true)} className="bg-[#C9A84C] text-[#001830] hover:bg-[#C9A84C]/90 font-medium" data-testid="create-event-btn">
            <Plus className="w-4 h-4 mr-2" /> Nuovo Evento
          </Button>
        )}
      </div>

      {/* Calendar Grid */}
      <div className="glass-card rounded-lg border border-white/8 overflow-hidden mb-6">
        <div className="grid grid-cols-7 border-b border-white/5">
          {["Lun","Mar","Mer","Gio","Ven","Sab","Dom"].map(d => (
            <div key={d} className="p-2 text-center text-[10px] font-semibold uppercase tracking-wider text-white/30">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {Array.from({ length: (firstDay + 6) % 7 }, (_, i) => (
            <div key={`e${i}`} className="min-h-[80px] p-1 border-b border-r border-white/[0.03]" />
          ))}
          {Array.from({ length: daysInMonth }, (_, i) => {
            const day = i + 1;
            const dateStr = `${year}-${String(month).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
            const dayEvents = eventiByDate[dateStr] || [];
            const isToday = dateStr === today;
            return (
              <div key={day} className={`min-h-[80px] p-1.5 border-b border-r border-white/[0.03] ${isToday ? 'bg-[#C9A84C]/[0.05]' : ''}`}>
                <p className={`text-xs font-medium mb-1 ${isToday ? 'text-[#C9A84C]' : 'text-white/40'}`}>{day}</p>
                {dayEvents.map(ev => (
                  <div key={ev.id} className="mb-1 px-1.5 py-0.5 rounded text-[9px] bg-[#00A0D1]/15 text-[#00A0D1] border border-[#00A0D1]/20 truncate cursor-default" title={`${ev.titolo}${ev.ora ? ' - ' + ev.ora : ''}`}>
                    {ev.ora && <span className="text-white/30 mr-1">{ev.ora}</span>}{ev.titolo}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>

      {/* Event list for current month */}
      <h3 className="text-sm font-semibold text-white mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Eventi del mese</h3>
      <div className="space-y-2">
        {eventi.filter(e => e.data?.startsWith(selectedMonth)).map(e => (
          <div key={e.id} className="glass-card rounded-lg border border-white/8 p-4 flex items-start gap-4" data-testid={`event-${e.id}`}>
            <div className="w-12 h-12 rounded-lg bg-[#00A0D1]/10 border border-[#00A0D1]/20 flex flex-col items-center justify-center flex-shrink-0">
              <p className="text-lg font-bold text-[#00A0D1] leading-none">{new Date(e.data + 'T00:00').getDate()}</p>
              <p className="text-[8px] uppercase text-[#00A0D1]/60">{new Date(e.data + 'T00:00').toLocaleDateString('it-IT', { month: 'short' })}</p>
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-white">{e.titolo}</p>
              <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-white/30">
                {e.ora && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{e.ora}</span>}
                {e.luogo && <span>{e.luogo}</span>}
                <Badge className="bg-white/5 text-white/40 border-white/10 text-[10px] border">{e.tipo}</Badge>
              </div>
              {e.descrizione && <p className="text-xs text-white/40 mt-1.5">{e.descrizione}</p>}
            </div>
            {isAdmin && (
              <Button size="sm" variant="ghost" onClick={() => handleDelete(e.id)} className="text-white/20 hover:text-red-400" data-testid={`delete-event-${e.id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>
        ))}
        {!eventi.filter(e => e.data?.startsWith(selectedMonth)).length && (
          <p className="text-center py-8 text-white/25">Nessun evento questo mese</p>
        )}
      </div>

      {/* Backup button for admin */}
      {isAdmin && (
        <div className="mt-8 glass-card rounded-lg border border-white/8 p-5">
          <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Download className="w-4 h-4 text-[#C9A84C]" /> Backup Dati
          </h3>
          <p className="text-xs text-white/30 mb-3">Scarica un backup completo di tutti i dati del sindacato in formato JSON</p>
          <Button size="sm" onClick={() => downloadAuthFile(`${API}/backup`, `backup_ugl_${new Date().toISOString().slice(0,10)}.json`)} className="bg-[#C9A84C] text-[#001830] hover:bg-[#C9A84C]/90 text-xs" data-testid="backup-btn">
            <Download className="w-3 h-3 mr-1.5" /> Scarica Backup Completo
          </Button>
        </div>
      )}

      {/* New Event Dialog */}
      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="sm:max-w-lg bg-[#0d1b2e] border-white/10 text-white" data-testid="new-event-dialog">
          <DialogHeader>
            <DialogTitle className="text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>Nuovo Evento</DialogTitle>
            <DialogDescription className="text-white/40">Aggiungi un evento al calendario</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-xs text-white/60">Titolo *</Label><Input data-testid="event-titolo" value={form.titolo} onChange={e => setForm(p => ({ ...p, titolo: e.target.value }))} className="mt-1 bg-white/5 border-white/10 text-white" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="text-xs text-white/60">Data *</Label><Input data-testid="event-data" type="date" value={form.data} onChange={e => setForm(p => ({ ...p, data: e.target.value }))} className="mt-1 bg-white/5 border-white/10 text-white" /></div>
              <div><Label className="text-xs text-white/60">Ora</Label><Input data-testid="event-ora" type="time" value={form.ora} onChange={e => setForm(p => ({ ...p, ora: e.target.value }))} className="mt-1 bg-white/5 border-white/10 text-white" /></div>
            </div>
            <div><Label className="text-xs text-white/60">Luogo</Label><Input data-testid="event-luogo" value={form.luogo} onChange={e => setForm(p => ({ ...p, luogo: e.target.value }))} className="mt-1 bg-white/5 border-white/10 text-white" /></div>
            <div><Label className="text-xs text-white/60">Tipo</Label>
              <Select value={form.tipo} onValueChange={v => setForm(p => ({ ...p, tipo: v }))}>
                <SelectTrigger className="mt-1 bg-white/5 border-white/10 text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0d1b2e] border-white/10 text-white">
                  <SelectItem value="evento">Evento</SelectItem>
                  <SelectItem value="riunione">Riunione</SelectItem>
                  <SelectItem value="scadenza">Scadenza</SelectItem>
                  <SelectItem value="assemblea">Assemblea</SelectItem>
                  <SelectItem value="formazione">Formazione</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs text-white/60">Descrizione</Label><Textarea data-testid="event-desc" value={form.descrizione} onChange={e => setForm(p => ({ ...p, descrizione: e.target.value }))} className="mt-1 bg-white/5 border-white/10 text-white" rows={2} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNew(false)} className="border-white/10 text-white/50 bg-transparent hover:bg-white/5">Annulla</Button>
            <Button data-testid="submit-event" onClick={handleCreate} disabled={saving} className="bg-[#C9A84C] text-[#001830] hover:bg-[#C9A84C]/90">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Crea Evento"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


/* ── Assistenza - Form Guidato Interattivo (Membro) ── */
const PROBLEMI = [
  { id: "licenziamento", label: "Licenziamento", icon: "Sei stato licenziato o temi un licenziamento?" },
  { id: "stipendio", label: "Mancato Pagamento", icon: "Non hai ricevuto lo stipendio o il TFR?" },
  { id: "mobbing", label: "Mobbing / Discriminazione", icon: "Subisci pressioni, vessazioni o discriminazioni?" },
  { id: "contratto", label: "Problemi Contrattuali", icon: "Il tuo contratto non è rispettato?" },
  { id: "infortunio", label: "Infortunio sul Lavoro", icon: "Hai avuto un infortunio o malattia professionale?" },
  { id: "conciliazione", label: "Conciliazione / Vertenza", icon: "Vuoi avviare una vertenza o conciliazione?" },
  { id: "conteggi", label: "Conteggi e Buste Paga", icon: "Hai dubbi sulle buste paga o i contributi?" },
  { id: "altro", label: "Altro Problema", icon: "Hai un altro tipo di problema lavorativo?" },
];

function AssistenzaView() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ tipo_problema: "", descrizione: "", urgenza: "normale", datore_lavoro: "", contratto: "", durata_problema: "", documenti_disponibili: "", preferenza_contatto: "telefono" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [mySegnalazioni, setMySegnalazioni] = useState([]);

  useEffect(() => { axios.get(`${API}/segnalazioni`).then(r => setMySegnalazioni(r.data)).catch(() => {}); }, [sent]);

  const submit = async () => {
    setSending(true);
    try {
      await axios.post(`${API}/segnalazioni`, form);
      toast.success("Segnalazione inviata! Ti contatteremo presto.");
      setSent(true);
    } catch { toast.error("Errore invio"); }
    setSending(false);
  };

  const botMsg = (text) => (
    <div className="flex gap-3 mb-4">
      <div className="w-8 h-8 rounded-full bg-[#C9A84C]/15 border border-[#C9A84C]/25 flex items-center justify-center flex-shrink-0">
        <Bot className="w-4 h-4 text-[#C9A84C]" />
      </div>
      <div className="bg-[#13243d] border border-[#1e3456] rounded-xl rounded-tl-none p-3 max-w-lg">
        <p className="text-sm text-white/70">{text}</p>
      </div>
    </div>
  );

  const statoColor = { nuova: "text-amber-400 border-amber-500/20 bg-amber-500/10", in_lavorazione: "text-[#5ba8d9] border-[#2a5080] bg-[#1a3a5c]", risolta: "text-emerald-400 border-emerald-500/20 bg-emerald-500/10" };

  if (sent) return (
    <div data-testid="assistenza-sent" className="text-center py-12">
      <div className="w-16 h-16 rounded-full bg-emerald-500/15 flex items-center justify-center mx-auto mb-4"><CheckCircle className="w-8 h-8 text-emerald-400" /></div>
      <h3 className="text-lg font-semibold text-white mb-2" style={{ fontFamily: 'Outfit' }}>Segnalazione Inviata!</h3>
      <p className="text-sm text-white/40 mb-6">Il sindacato è stato notificato. Ti contatteremo al più presto.</p>
      <Button onClick={() => { setSent(false); setStep(0); setForm({ tipo_problema: "", descrizione: "", urgenza: "normale", datore_lavoro: "", contratto: "", durata_problema: "", documenti_disponibili: "", preferenza_contatto: "telefono" }); }}
        className="bg-[#1a3a5c] text-[#5ba8d9] border border-[#2a5080]">Nuova Segnalazione</Button>
    </div>
  );

  return (
    <div data-testid="assistenza-view">
      {/* Le mie segnalazioni precedenti */}
      {mySegnalazioni.length > 0 && step === 0 && (
        <div className="mb-6">
          <p className="text-[10px] text-white/30 uppercase mb-2">Le tue segnalazioni</p>
          <div className="space-y-2">{mySegnalazioni.slice(0, 3).map(s => (
            <div key={s.id} className="bg-[#13243d] border border-[#1e3456] rounded-lg p-3 flex items-center gap-3">
              <AlertTriangle className="w-4 h-4 text-white/25 flex-shrink-0" />
              <div className="flex-1 min-w-0"><p className="text-xs text-white/60 truncate">{s.tipo_problema}</p><p className="text-[10px] text-white/25">{new Date(s.created_at).toLocaleDateString('it-IT')}</p></div>
              <Badge className={`text-[10px] border ${statoColor[s.stato] || statoColor.nuova}`}>{s.stato}</Badge>
            </div>
          ))}</div>
        </div>
      )}

      {/* Step 0: Scegli tipo problema */}
      {step === 0 && (<>
        {botMsg("Ciao! Sono l'assistente UGL Lariano. In cosa posso aiutarti oggi? Seleziona il tipo di problema:")}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 ml-11">
          {PROBLEMI.map(p => (
            <button key={p.id} onClick={() => { setForm(f => ({ ...f, tipo_problema: p.label })); setStep(1); }}
              className="bg-[#13243d] border border-[#1e3456] rounded-xl p-4 text-left hover:border-[#C9A84C]/30 hover:bg-[#C9A84C]/[0.03] transition-all group" data-testid={`problema-${p.id}`}>
              <p className="text-sm font-medium text-white group-hover:text-[#C9A84C] transition-colors">{p.label}</p>
              <p className="text-[10px] text-white/30 mt-0.5">{p.icon}</p>
            </button>
          ))}
        </div>
      </>)}

      {/* Step 1: Urgenza */}
      {step === 1 && (<>
        {botMsg(`Hai selezionato: "${form.tipo_problema}". Quanto è urgente la situazione?`)}
        <div className="flex gap-2 ml-11">
          {[{v:"urgente",l:"Urgente",c:"border-red-500/30 text-red-400 hover:bg-red-500/10"},{v:"normale",l:"Normale",c:"border-[#C9A84C]/30 text-[#C9A84C] hover:bg-[#C9A84C]/10"},{v:"informativa",l:"Solo Info",c:"border-[#5ba8d9]/30 text-[#5ba8d9] hover:bg-[#5ba8d9]/10"}].map(u => (
            <button key={u.v} onClick={() => { setForm(f => ({ ...f, urgenza: u.v })); setStep(2); }}
              className={`flex-1 border rounded-xl p-3 text-center text-sm font-medium bg-transparent transition-all ${u.c}`}>{u.l}</button>
          ))}
        </div>
      </>)}

      {/* Step 2: Datore lavoro + contratto */}
      {step === 2 && (<>
        {botMsg("Perfetto. Puoi dirmi il nome del datore di lavoro e che tipo di contratto hai?")}
        <div className="ml-11 space-y-3 max-w-lg">
          <Input placeholder="Nome datore di lavoro (opzionale)" value={form.datore_lavoro} onChange={e => setForm(f => ({ ...f, datore_lavoro: e.target.value }))}
            className="bg-white/5 border-[#1e3456] text-white placeholder:text-white/20" />
          <select value={form.contratto} onChange={e => setForm(f => ({ ...f, contratto: e.target.value }))}
            className="w-full h-9 rounded-md bg-white/5 border border-[#1e3456] text-white text-sm px-3">
            <option value="" className="bg-[#13243d]">Tipo contratto...</option>
            <option value="indeterminato" className="bg-[#13243d]">Tempo Indeterminato</option>
            <option value="determinato" className="bg-[#13243d]">Tempo Determinato</option>
            <option value="apprendistato" className="bg-[#13243d]">Apprendistato</option>
            <option value="partita_iva" className="bg-[#13243d]">Partita IVA</option>
            <option value="cococo" className="bg-[#13243d]">Co.Co.Co.</option>
            <option value="stage" className="bg-[#13243d]">Stage/Tirocinio</option>
            <option value="altro" className="bg-[#13243d]">Altro</option>
          </select>
          <Button onClick={() => setStep(3)} className="bg-[#C9A84C] text-[#001830] hover:bg-[#C9A84C]/90">Avanti <ChevronRight className="w-4 h-4 ml-1" /></Button>
        </div>
      </>)}

      {/* Step 3: Descrizione dettagliata */}
      {step === 3 && (<>
        {botMsg("Ora raccontami nel dettaglio cosa è successo. Più informazioni mi dai, meglio potremo aiutarti.")}
        <div className="ml-11 space-y-3 max-w-lg">
          <Textarea value={form.descrizione} onChange={e => setForm(f => ({ ...f, descrizione: e.target.value }))} rows={4}
            placeholder="Descrivi la situazione nel dettaglio..."
            className="bg-white/5 border-[#1e3456] text-white placeholder:text-white/20" />
          <select value={form.durata_problema} onChange={e => setForm(f => ({ ...f, durata_problema: e.target.value }))}
            className="w-full h-9 rounded-md bg-white/5 border border-[#1e3456] text-white text-sm px-3">
            <option value="" className="bg-[#13243d]">Da quanto tempo dura?</option>
            <option value="giorni" className="bg-[#13243d]">Da pochi giorni</option>
            <option value="settimane" className="bg-[#13243d]">Da alcune settimane</option>
            <option value="mesi" className="bg-[#13243d]">Da mesi</option>
            <option value="anni" className="bg-[#13243d]">Da oltre un anno</option>
          </select>
          <Button onClick={() => setStep(4)} disabled={!form.descrizione} className="bg-[#C9A84C] text-[#001830] hover:bg-[#C9A84C]/90">Avanti <ChevronRight className="w-4 h-4 ml-1" /></Button>
        </div>
      </>)}

      {/* Step 4: Documenti + preferenza contatto */}
      {step === 4 && (<>
        {botMsg("Ultimo passo! Hai documenti da condividere e come preferisci essere contattato?")}
        <div className="ml-11 space-y-3 max-w-lg">
          <select value={form.documenti_disponibili} onChange={e => setForm(f => ({ ...f, documenti_disponibili: e.target.value }))}
            className="w-full h-9 rounded-md bg-white/5 border border-[#1e3456] text-white text-sm px-3">
            <option value="" className="bg-[#13243d]">Documenti disponibili...</option>
            <option value="buste_paga" className="bg-[#13243d]">Buste paga</option>
            <option value="contratto" className="bg-[#13243d]">Contratto di lavoro</option>
            <option value="lettere" className="bg-[#13243d]">Lettere/comunicazioni</option>
            <option value="referti" className="bg-[#13243d]">Referti medici</option>
            <option value="nessuno" className="bg-[#13243d]">Nessun documento al momento</option>
          </select>
          <div className="flex gap-2">
            {[{v:"telefono",l:"Telefono"},{v:"whatsapp",l:"WhatsApp"},{v:"messaggio",l:"Messaggio interno"}].map(c => (
              <button key={c.v} onClick={() => setForm(f => ({ ...f, preferenza_contatto: c.v }))}
                className={`flex-1 border rounded-lg p-2.5 text-xs font-medium text-center transition-all ${form.preferenza_contatto === c.v ? 'border-[#C9A84C] text-[#C9A84C] bg-[#C9A84C]/10' : 'border-[#1e3456] text-white/40 bg-transparent hover:border-white/20'}`}>{c.l}</button>
            ))}
          </div>
          <Button onClick={submit} disabled={sending} className="w-full bg-[#C9A84C] text-[#001830] hover:bg-[#C9A84C]/90 h-11 font-semibold">
            {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />} Invia Segnalazione
          </Button>
        </div>
      </>)}

      {step > 0 && step < 4 && (
        <button onClick={() => setStep(s => s - 1)} className="ml-11 mt-3 text-xs text-white/30 hover:text-white/60 transition-colors">&larr; Torna indietro</button>
      )}
    </div>
  );
}

/* ── Segnalazioni Admin View con Dettaglio ── */
function SegnalazioniAdminView() {
  const [segnalazioni, setSegnalazioni] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState(null);
  const [nota, setNota] = useState("");
  const [userInfo, setUserInfo] = useState(null);
  const [showArchiviate, setShowArchiviate] = useState(false);

  useEffect(() => { axios.get(`${API}/segnalazioni`).then(r => setSegnalazioni(r.data)).catch(() => {}).finally(() => setLoading(false)); }, []);

  const updateStato = async (id, stato) => {
    try {
      await axios.put(`${API}/segnalazioni/${id}/stato?stato=${stato}`, {});
      setSegnalazioni(p => p.map(s => s.id === id ? { ...s, stato } : s));
      if (detail?.id === id) setDetail(d => ({ ...d, stato }));
      toast.success("Stato aggiornato");
    } catch { toast.error("Errore"); }
  };

  const addNota = async () => {
    if (!nota.trim() || !detail) return;
    try {
      await axios.post(`${API}/commenti`, { testo: nota, riferimento_tipo: "segnalazione", riferimento_id: detail.id });
      setNota("");
      toast.success("Nota aggiunta");
    } catch { toast.error("Errore"); }
  };

  const openDetail = async (s) => {
    setDetail(s);
    try { const { data } = await axios.get(`${API}/admin/users/${s.user_id}/scheda`); setUserInfo(data); } catch { setUserInfo(null); }
  };

  const sc = { nuova: "text-amber-400 border-amber-500/20 bg-amber-500/10", in_lavorazione: "text-[#5ba8d9] border-[#2a5080] bg-[#1a3a5c]", risolta: "text-emerald-400 border-emerald-500/20 bg-emerald-500/10", archiviata: "text-white/25 border-white/10 bg-white/5" };
  const uc = { urgente: "border-red-500/30 bg-red-500/10 text-red-400", normale: "border-[#C9A84C]/30 bg-[#C9A84C]/10 text-[#C9A84C]", informativa: "border-[#5ba8d9]/30 bg-[#5ba8d9]/10 text-[#5ba8d9]" };
  const filteredSgn = segnalazioni.filter(s => showArchiviate || s.stato !== "archiviata").filter(s => `${s.user_nome} ${s.tipo_problema} ${s.descrizione}`.toLowerCase().includes(search.toLowerCase()));
  const pg = usePagination(filteredSgn);

  if (loading) return <div className="text-center py-12 text-white/30">Caricamento...</div>;

  // ─── Detail View ───
  if (detail) {
    return (
      <div data-testid="segnalazione-detail">
        <button onClick={() => setDetail(null)} className="flex items-center gap-1.5 text-sm text-white/40 hover:text-white mb-4"><ArrowLeft className="w-4 h-4" /> Torna alla lista</button>

        <div className="bg-[#13243d] border border-[#1e3456] rounded-xl p-6 shadow-lg shadow-black/20 mb-4">
          <div className="flex items-start justify-between mb-5">
            <div>
              <div className="flex items-center gap-3 mb-1">
                <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>{detail.tipo_problema}</h2>
                <Badge className={`text-xs border ${uc[detail.urgenza] || uc.normale}`}>{detail.urgenza}</Badge>
              </div>
              <p className="text-xs text-white/30">da {detail.user_nome} - {new Date(detail.created_at).toLocaleString('it-IT')}</p>
            </div>
            <Badge className={`text-xs border ${sc[detail.stato] || sc.nuova}`}>{detail.stato}</Badge>
          </div>

          {/* Contatti Utente */}
          {userInfo && (
            <div className="bg-gradient-to-r from-[#1a3050] to-[#1a3a5c] border border-[#2a5080] rounded-xl p-4 mb-5 flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-[#C9A84C]/15 border border-[#C9A84C]/25 flex items-center justify-center flex-shrink-0">
                <User className="w-5 h-5 text-[#C9A84C]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-white">{userInfo.nome} {userInfo.cognome}</p>
                <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-white/50">
                  {userInfo.telefono && <span className="flex items-center gap-1">Tel: {userInfo.telefono}</span>}
                  {userInfo.email && <span className="flex items-center gap-1">Email: {userInfo.email}</span>}
                  {userInfo.codice_fiscale && <span className="font-mono text-[10px] text-white/30">{userInfo.codice_fiscale}</span>}
                </div>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                {userInfo.telefono && (
                  <a href={`https://wa.me/39${userInfo.telefono.replace(/\s/g, '')}`} target="_blank" rel="noopener noreferrer">
                    <Button size="sm" className="bg-[#25D366] text-white hover:bg-[#25D366]/90 text-xs h-8"><MessageSquare className="w-3 h-3 mr-1" /> WhatsApp</Button>
                  </a>
                )}
                {userInfo.telefono && (
                  <a href={`tel:${userInfo.telefono}`}>
                    <Button size="sm" className="bg-[#5ba8d9] text-white hover:bg-[#5ba8d9]/90 text-xs h-8">Chiama</Button>
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Info Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
            {[
              { l: "Datore di Lavoro", v: detail.datore_lavoro || "Non specificato" },
              { l: "Contratto", v: detail.contratto || "Non specificato" },
              { l: "Durata Problema", v: detail.durata_problema || "Non specificata" },
              { l: "Preferenza Contatto", v: detail.preferenza_contatto || "Telefono" },
            ].map((item, i) => (
              <div key={i} className="bg-[#0f1d32] rounded-lg p-3 border border-[#1e3456]">
                <p className="text-[10px] text-white/30 uppercase mb-1">{item.l}</p>
                <p className="text-sm text-white/60 font-medium capitalize">{item.v}</p>
              </div>
            ))}
          </div>

          {/* Descrizione */}
          <div className="mb-5">
            <p className="text-[10px] text-white/30 uppercase mb-2">Descrizione del Problema</p>
            <div className="bg-[#0f1d32] rounded-lg p-4 border border-[#1e3456]">
              <p className="text-sm text-white/60 leading-relaxed whitespace-pre-wrap">{detail.descrizione}</p>
            </div>
          </div>

          {detail.documenti_disponibili && (
            <div className="mb-5">
              <p className="text-[10px] text-white/30 uppercase mb-2">Documenti Disponibili</p>
              <p className="text-sm text-white/50 capitalize">{detail.documenti_disponibili}</p>
            </div>
          )}

          {/* Azioni Stato */}
          <div className="flex flex-wrap gap-2 mb-5">
            <p className="text-[10px] text-white/30 uppercase self-center mr-2">Cambia Stato:</p>
            {[{v:"nuova",l:"Nuova",c:"border-amber-500/30 text-amber-400"},{v:"in_lavorazione",l:"In Lavorazione",c:"border-[#2a5080] text-[#5ba8d9]"},{v:"risolta",l:"Risolta",c:"border-emerald-500/20 text-emerald-400"}].map(st => (
              <Button key={st.v} size="sm" onClick={() => updateStato(detail.id, st.v)}
                className={`text-xs border bg-transparent hover:bg-white/5 ${detail.stato === st.v ? st.c + ' ring-1 ring-white/10' : 'border-[#1e3456] text-white/30'}`}
                data-testid={`set-stato-${st.v}`}>
                {detail.stato === st.v && <CheckCircle className="w-3 h-3 mr-1" />} {st.l}
              </Button>
            ))}
            <div className="ml-auto">
              <Button size="sm" onClick={() => { updateStato(detail.id, "archiviata"); setDetail(null); }}
                className="text-xs border border-white/10 bg-transparent text-white/30 hover:bg-white/5" data-testid="archivia-btn">
                Archivia
              </Button>
            </div>
          </div>

          {/* Note Interne */}
          <div>
            <p className="text-[10px] text-white/30 uppercase mb-2">Note Interne</p>
            <CommentiInline tipo="segnalazione" rifId={detail.id} />
          </div>
        </div>
      </div>
    );
  }

  // ─── List View ───
  const archiviateCount = segnalazioni.filter(s => s.stato === "archiviata").length;

  return (
    <div data-testid="segnalazioni-admin">
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/20" /><Input placeholder="Cerca segnalazioni..." value={search} onChange={e => { setSearch(e.target.value); pg.resetPage(); }} className="pl-10 bg-white/5 border-[#1e3456] text-white placeholder:text-white/20" /></div>
        {archiviateCount > 0 && (
          <Button size="sm" variant="outline" onClick={() => setShowArchiviate(!showArchiviate)}
            className={`text-xs border-[#1e3456] bg-transparent ${showArchiviate ? 'text-white/60' : 'text-white/30'} hover:bg-[#1a3a5c]`} data-testid="toggle-archiviate">
            {showArchiviate ? "Nascondi" : "Mostra"} archiviate ({archiviateCount})
          </Button>
        )}
      </div>
      {!pg.paged.length && <p className="text-center py-12 text-white/30">Nessuna segnalazione</p>}
      <div className="space-y-2">{pg.paged.map(s => (
        <div key={s.id} onClick={() => openDetail(s)} className="bg-[#13243d] border border-[#1e3456] rounded-xl p-4 cursor-pointer hover:border-[#2a5080] hover:shadow-lg hover:shadow-black/15 transition-all" data-testid={`segn-${s.id}`}>
          <div className="flex items-center gap-4">
            <div className={`w-2 h-2 rounded-full flex-shrink-0 ${s.urgenza === 'urgente' ? 'bg-red-400' : s.urgenza === 'informativa' ? 'bg-[#5ba8d9]' : 'bg-[#C9A84C]'}`} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <p className="text-sm font-medium text-white truncate">{s.tipo_problema}</p>
                <Badge className={`text-[9px] border ${sc[s.stato] || sc.nuova}`}>{s.stato}</Badge>
              </div>
              <p className="text-[10px] text-white/30">da {s.user_nome} - {new Date(s.created_at).toLocaleDateString('it-IT')}</p>
            </div>
            <p className="text-xs text-white/20 truncate max-w-[200px] hidden md:block">{s.descrizione}</p>
            <ChevronRight className="w-4 h-4 text-white/15 flex-shrink-0" />
          </div>
        </div>
      ))}</div>
      <PaginationBar {...pg} />
    </div>
  );
}

/* ── Commenti Inline (per segnalazioni) ── */
function CommentiInline({ tipo, rifId }) {
  const [commenti, setCommenti] = useState([]);
  const [nc, setNc] = useState("");
  const [s, setS] = useState(false);
  useEffect(() => { if (rifId) axios.get(`${API}/commenti/${tipo}/${rifId}`).then(r => setCommenti(r.data)).catch(() => {}); }, [tipo, rifId]);
  const send = async () => { if (!nc.trim()) return; setS(true); try { const { data } = await axios.post(`${API}/commenti`, { testo: nc, riferimento_tipo: tipo, riferimento_id: rifId }); setCommenti(p => [...p, data]); setNc(""); } catch (e) { console.error(e); } setS(false); };
  return (
    <div>
      <div className="space-y-2 mb-3 max-h-48 overflow-y-auto">{commenti.map(c => (
        <div key={c.id} className="bg-[#0f1d32] rounded-lg p-3 border border-[#1e3456]">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-5 h-5 rounded-full bg-[#1a3a5c] flex items-center justify-center text-[8px] font-bold text-[#5ba8d9]">{c.autore_nome?.split(' ').map(w=>w[0]).join('')}</div>
            <span className="text-xs text-white/60">{c.autore_nome}</span>
            <span className="text-[9px] text-white/20">{new Date(c.created_at).toLocaleString('it-IT',{day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit'})}</span>
          </div>
          <p className="text-sm text-white/50 ml-7">{c.testo}</p>
        </div>
      ))}</div>
      <div className="flex gap-2">
        <Input value={nc} onChange={e => setNc(e.target.value)} placeholder="Aggiungi nota interna..." className="bg-white/5 border-[#1e3456] text-white placeholder:text-white/20 text-sm" onKeyDown={e => e.key==='Enter' && send()} />
        <Button onClick={send} disabled={s} size="sm" className="bg-[#1a3a5c] text-[#5ba8d9] hover:bg-[#2a5080] border border-[#2a5080]">{s?<Loader2 className="w-3.5 h-3.5 animate-spin"/>:<Send className="w-3.5 h-3.5"/>}</Button>
      </div>
    </div>
  );
}


/* ── Audit Log View ── */
function AuditLogView() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => { axios.get(`${API}/audit-log`).then(r => setLogs(r.data)).catch(() => {}).finally(() => setLoading(false)); }, []);

  const searched = logs.filter(l => `${l.user_name} ${l.azione} ${l.dettaglio}`.toLowerCase().includes(search.toLowerCase()));
  const pg = usePagination(searched);

  const downloadCSV = () => {
    const header = "Data;Ora;Azione;Dettaglio;Utente;IP;Luogo\n";
    const rows = pg.paged.map(l => {
      const d = new Date(l.timestamp);
      return `"${d.toLocaleDateString('it-IT')}";"${d.toLocaleTimeString('it-IT')}";"${l.azione}";"${(l.dettaglio || '').replace(/"/g, '""')}";"${l.user_name}";"${l.ip || ''}";"${l.luogo || ''}"`;
    }).join("\n");
    const blob = new Blob(['\ufeff' + header + rows], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `audit_log_ugl.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const actionColors = { login: "text-[#5ba8d9]", crea_utente: "text-emerald-400", elimina_utente: "text-red-400", tessera: "text-[#C9A84C]", privacy: "text-purple-400", segnalazione: "text-amber-400" };

  if (loading) return <div className="text-center py-12 text-white/30">Caricamento...</div>;

  return (
    <div data-testid="audit-log-view">
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/20" /><Input placeholder="Cerca nei log..." value={search} onChange={e => { setSearch(e.target.value); pg.resetPage(); }} className="pl-10 bg-white/5 border-[#1e3456] text-white placeholder:text-white/20" /></div>
        <Button size="sm" onClick={downloadCSV} className="bg-[#C9A84C] text-[#001830] hover:bg-[#C9A84C]/90 text-xs h-9" data-testid="audit-download">
          <Download className="w-3 h-3 mr-1.5" /> CSV
        </Button>
      </div>
      {!pg.paged.length && <p className="text-center py-12 text-white/30">Nessun log</p>}
      <div className="space-y-1.5">
        {pg.paged.map(l => (
          <div key={l.id} className="bg-[#13243d] border border-[#1e3456] rounded-lg px-4 py-2.5 flex items-center gap-3" data-testid={`log-${l.id}`}>
            <div className="w-1.5 h-1.5 rounded-full bg-white/20 flex-shrink-0" />
            <p className="text-xs text-white/30 w-32 flex-shrink-0">{new Date(l.timestamp).toLocaleString('it-IT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit' })}</p>
            <p className={`text-xs font-semibold w-28 flex-shrink-0 ${actionColors[l.azione] || 'text-white/50'}`}>{l.azione}</p>
            <p className="text-xs text-white/60 flex-1 truncate">{l.dettaglio}</p>
            {l.ip && <p className="text-[10px] text-[#5ba8d9]/50 font-mono flex-shrink-0">{l.ip}</p>}
            {l.luogo && <p className="text-[10px] text-[#C9A84C]/50 flex-shrink-0">{l.luogo}</p>}
            <p className="text-[10px] text-white/25 flex-shrink-0">{l.user_name}</p>
          </div>
        ))}
      </div>
      <PaginationBar {...pg} />
    </div>
  );
}


/* ── Analytics Dashboard Premium ── */
function AnalyticsDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    axios.get(`${API}/analytics`)
      .then(r => setData(r.data))
      .catch(e => { console.error('Analytics error:', e); setError(true); })
      .finally(() => setLoading(false));
  }, []);

  const exportAnalytics = () => {
    if (!data) return;
    let csv = "\ufeffMetrica;Valore\n";
    csv += `"Visite Totali";"${data.total}"\n\n`;
    csv += "Data;Visite\n";
    (data.daily || []).forEach(d => { csv += `"${d.date}";"${d.views}"\n`; });
    csv += "\nOra;Visite\n";
    (data.hourly || []).forEach(h => { csv += `"${h.hour}:00";"${h.views}"\n`; });
    csv += "\nCitta;Paese;Visite\n";
    (data.geo || []).forEach(g => { csv += `"${g.city}";"${g.country}";"${g.views}"\n`; });
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'analytics_ugl.csv';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) return <div className="bg-[#13243d] border border-[#1e3456] rounded-xl p-8 text-center text-white/30">Caricamento analytics...</div>;
  if (error || !data) return (
    <div className="bg-[#13243d] border border-[#1e3456] rounded-xl p-8 text-center">
      <Eye className="w-8 h-8 text-white/20 mx-auto mb-2" />
      <p className="text-sm text-white/40">Analytics non disponibili</p>
      <p className="text-xs text-white/20 mt-1">I dati saranno visibili dopo le prime visite al sito</p>
    </div>
  );

  const dailyReversed = [...(data.daily || [])].reverse().slice(-14);
  const maxDaily = Math.max(...dailyReversed.map(d => d.views), 1);
  const maxHourly = Math.max(...(data.hourly || []).map(h => h.views), 1);
  const totalGeo = (data.geo || []).reduce((s, g) => s + g.views, 0) || 1;

  return (
    <div className="space-y-4" data-testid="analytics-dashboard">
      {/* Header */}
      <div className="relative overflow-hidden bg-gradient-to-br from-[#13243d] to-[#0f1d32] border border-[#1e3456] rounded-2xl p-6" style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.05)' }}>
        <div className="absolute inset-0 bg-gradient-to-b from-white/[0.02] to-transparent pointer-events-none" />
        <div className="relative flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-white flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <Eye className="w-5 h-5 text-emerald-400" /> Analytics & Traffico
            </h3>
            <p className="text-xs text-white/30 mt-1">Report dettagliato delle visite al sito</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-3xl font-bold text-emerald-400" style={{ fontFamily: 'Outfit' }}>{data.total.toLocaleString('it-IT')}</p>
              <p className="text-[10px] text-white/30 uppercase tracking-wider">Visite Totali</p>
            </div>
            <Button size="sm" onClick={exportAnalytics} className="bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25 border border-emerald-500/20 text-xs" data-testid="export-analytics">
              <Download className="w-3 h-3 mr-1" /> Report
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Visite Giornaliere */}
        <div className="relative overflow-hidden bg-[#13243d] border border-[#1e3456] rounded-2xl p-5" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.03)' }}>
          <div className="absolute inset-0 bg-gradient-to-b from-white/[0.015] to-transparent pointer-events-none rounded-2xl" />
          <h4 className="text-xs font-semibold text-white/60 uppercase tracking-wider mb-4 relative">Visite Giornaliere</h4>
          {/* SVG trend line */}
          <div className="relative">
            <svg viewBox={`0 0 ${dailyReversed.length * 50} 120`} className="absolute inset-0 w-full h-28 z-10 pointer-events-none" preserveAspectRatio="none">
              {dailyReversed.length > 1 && (
                <>
                  <defs>
                    <linearGradient id="lineGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#10b981" stopOpacity="0.3" />
                      <stop offset="100%" stopColor="#10b981" stopOpacity="1" />
                    </linearGradient>
                    <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#10b981" stopOpacity="0.15" />
                      <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  <path d={`M ${dailyReversed.map((d, i) => `${i * 50 + 25} ${120 - (d.views / maxDaily) * 100}`).join(' L ')} L ${(dailyReversed.length - 1) * 50 + 25} 120 L 25 120 Z`} fill="url(#areaGrad)" />
                  <polyline points={dailyReversed.map((d, i) => `${i * 50 + 25},${120 - (d.views / maxDaily) * 100}`).join(' ')} fill="none" stroke="url(#lineGrad)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                  {dailyReversed.map((d, i) => (
                    <circle key={i} cx={i * 50 + 25} cy={120 - (d.views / maxDaily) * 100} r="3" fill="#10b981" stroke="#13243d" strokeWidth="2" />
                  ))}
                </>
              )}
            </svg>
            <div className="flex items-end gap-1 h-28 relative">
              {dailyReversed.map((d, i) => {
                const h = (d.views / maxDaily) * 100;
                return (
                  <div key={i} className="flex-1 flex flex-col items-center gap-0.5 group relative z-20">
                    <span className="text-[9px] text-emerald-400 font-bold">{d.views}</span>
                    <div className="w-full rounded-t relative overflow-hidden transition-all group-hover:brightness-125" style={{ height: `${Math.max(h, 4)}%` }}>
                      <div className="absolute inset-0 bg-gradient-to-t from-emerald-700/60 to-emerald-500/30 rounded-t" />
                    </div>
                    <span className="text-[7px] text-white/25">{d.date?.slice(5).replace('-','/')}</span>
                  </div>
                );
              })}
            </div>
          </div>
          {/* Reflection */}
          <div className="flex items-start gap-1 h-5 opacity-15 scale-y-[-1]" style={{ maskImage: 'linear-gradient(to bottom, rgba(0,0,0,0.3), transparent)', WebkitMaskImage: 'linear-gradient(to bottom, rgba(0,0,0,0.3), transparent)' }}>
            {dailyReversed.map((d, i) => <div key={i} className="flex-1"><div className="w-full rounded-t bg-emerald-500/30" style={{ height: `${(d.views / maxDaily) * 30}%` }} /></div>)}
          </div>
        </div>

        {/* Visite per Ora */}
        <div className="relative overflow-hidden bg-[#13243d] border border-[#1e3456] rounded-2xl p-5" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.03)' }}>
          <div className="absolute inset-0 bg-gradient-to-b from-white/[0.015] to-transparent pointer-events-none rounded-2xl" />
          <h4 className="text-xs font-semibold text-white/60 uppercase tracking-wider mb-4 relative">Distribuzione Oraria</h4>
          <div className="relative">
            <svg viewBox={`0 0 ${24 * 20} 120`} className="absolute inset-0 w-full h-28 z-10 pointer-events-none" preserveAspectRatio="none">
              <defs>
                <linearGradient id="hourLineGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#5ba8d9" stopOpacity="0.3" />
                  <stop offset="50%" stopColor="#5ba8d9" stopOpacity="1" />
                  <stop offset="100%" stopColor="#5ba8d9" stopOpacity="0.3" />
                </linearGradient>
                <linearGradient id="hourAreaGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#5ba8d9" stopOpacity="0.1" />
                  <stop offset="100%" stopColor="#5ba8d9" stopOpacity="0" />
                </linearGradient>
              </defs>
              <path d={`M ${(data.hourly || []).map((h, i) => `${i * 20 + 10} ${120 - (h.views / maxHourly) * 95}`).join(' L ')} L ${23 * 20 + 10} 120 L 10 120 Z`} fill="url(#hourAreaGrad)" />
              <polyline points={(data.hourly || []).map((h, i) => `${i * 20 + 10},${120 - (h.views / maxHourly) * 95}`).join(' ')} fill="none" stroke="url(#hourLineGrad)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <div className="flex items-end gap-px h-28 relative">
              {(data.hourly || []).map((h, i) => {
                const height = (h.views / maxHourly) * 100;
                const isActive = h.views > 0;
                return (
                  <div key={i} className="flex-1 flex flex-col items-center gap-0.5 group relative z-20" title={`${h.hour}:00 - ${h.views} visite`}>
                    {isActive && <span className="text-[7px] text-[#5ba8d9] font-bold">{h.views}</span>}
                    <div className="w-full rounded-t transition-all group-hover:brightness-150" style={{ height: `${Math.max(height, isActive ? 4 : 1)}%` }}>
                      <div className={`w-full h-full rounded-t ${isActive ? 'bg-gradient-to-t from-[#2a5080]/60 to-[#5ba8d9]/30' : 'bg-[#1e3456]/50'}`} />
                    </div>
                    {i % 3 === 0 && <span className="text-[6px] text-white/20">{h.hour}</span>}
                  </div>
                );
              })}
            </div>
          </div>
          {/* Reflection */}
          <div className="flex items-start gap-px h-4 opacity-15 scale-y-[-1]" style={{ maskImage: 'linear-gradient(to bottom, rgba(0,0,0,0.3), transparent)', WebkitMaskImage: 'linear-gradient(to bottom, rgba(0,0,0,0.3), transparent)' }}>
            {(data.hourly || []).map((h, i) => <div key={i} className="flex-1"><div className="w-full rounded-t bg-[#5ba8d9]/20" style={{ height: `${(h.views / maxHourly) * 30}%` }} /></div>)}
          </div>
        </div>
      </div>

      {/* Geo - Citta e Paesi */}
      {(data.geo || []).length > 0 && (
        <div className="relative overflow-hidden bg-[#13243d] border border-[#1e3456] rounded-2xl p-5" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.03)' }}>
          <div className="absolute inset-0 bg-gradient-to-b from-white/[0.015] to-transparent pointer-events-none rounded-2xl" />
          <h4 className="text-xs font-semibold text-white/60 uppercase tracking-wider mb-4 relative flex items-center gap-2">
            <MapPin className="w-3.5 h-3.5 text-[#C9A84C]" /> Provenienza Geografica
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 relative">
            {data.geo.map((g, i) => {
              const pct = (g.views / totalGeo) * 100;
              return (
                <div key={i} className="flex items-center gap-3 p-2.5 rounded-xl bg-[#0f1d32] border border-[#1e3456] group hover:border-[#2a5080] transition-all">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#1a3a5c] to-[#0f2a45] border border-[#2a5080] flex items-center justify-center flex-shrink-0">
                    <span className="text-[9px] font-bold text-[#5ba8d9]">{i + 1}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-white truncate">{g.city || 'Sconosciuta'}</p>
                    <p className="text-[9px] text-white/25">{g.country}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <div className="w-16 h-1.5 bg-[#1e3456] rounded-full overflow-hidden">
                      <div className="h-full rounded-full bg-gradient-to-r from-[#C9A84C] to-[#e0c060]" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-xs font-bold text-[#C9A84C] w-8 text-right">{g.views}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}


/* ── PinGate - Richiede PIN per accedere ── */
function PinGate({ userId, children }) {
  const [verified, setVerified] = useState(false);
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [checking, setChecking] = useState(false);

  // Reset when tab changes
  useEffect(() => { setVerified(false); setPin(""); setError(""); }, [userId]);

  const verify = async () => {
    if (pin.length !== 8) { setError("Il PIN deve essere di 8 cifre"); return; }
    setChecking(true); setError("");
    try {
      await axios.post(`${API}/auth/verify-pin`, { pin });
      setVerified(true);
    } catch (err) {
      setError(err.response?.data?.detail || "PIN non valido");
    }
    setChecking(false);
  };

  if (verified) return children;

  return (
    <div className="flex items-center justify-center py-16">
      <div className="w-full max-w-sm text-center">
        <div className="w-14 h-14 rounded-full bg-[#C9A84C]/15 border border-[#C9A84C]/25 flex items-center justify-center mx-auto mb-4">
          <Lock className="w-7 h-7 text-[#C9A84C]" />
        </div>
        <h3 className="text-lg font-bold text-white mb-1" style={{ fontFamily: 'Outfit' }}>Area Protetta</h3>
        <p className="text-xs text-white/40 mb-6">Inserisci il tuo PIN a 8 cifre per accedere ai documenti</p>

        <div className="bg-[#13243d] border border-[#1e3456] rounded-xl p-5" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.2)' }}>
          <div className="flex justify-center mb-4">
            <div className="flex gap-1">
              {[0,1,2,3,4,5,6,7].map(i => (
                <div key={i} className={`w-8 h-10 rounded-lg border flex items-center justify-center text-lg font-bold transition-all ${pin[i] ? 'border-[#C9A84C]/40 bg-[#C9A84C]/10 text-[#C9A84C]' : 'border-[#1e3456] bg-[#0f1d32] text-white/10'}`}>
                  {pin[i] ? '*' : ''}
                </div>
              ))}
            </div>
          </div>
          <Input
            type="password" inputMode="numeric" maxLength={8}
            value={pin} onChange={e => { setPin(e.target.value.replace(/\D/g, '').slice(0, 8)); setError(""); }}
            onKeyDown={e => e.key === 'Enter' && verify()}
            placeholder="Inserisci PIN"
            className="text-center tracking-[0.3em] text-lg bg-white/5 border-[#1e3456] text-white placeholder:text-white/15 mb-3"
            data-testid="pin-gate-input"
            autoFocus
          />
          {error && <p className="text-xs text-red-400 mb-3">{error}</p>}
          <Button onClick={verify} disabled={checking || pin.length !== 8} className="w-full bg-[#C9A84C] text-[#001830] hover:bg-[#C9A84C]/90 font-semibold disabled:opacity-30" data-testid="pin-gate-submit">
            {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verifica PIN"}
          </Button>
        </div>
      </div>
    </div>
  );
}
